'use client'

import { Spinner } from '@/components/atoms'
import PaymentAPIService from '@/services/PaymentAPIService'
import debugLogger from '@/utils/debugLogger'
import {
  ERROR_REDIRECT_DELAY_MS,
  POPUPS_CLOSE_DELAY_MS,
  REDIRECT_DELAY_MS,
} from '@/utils/paymentConstants'
import {
  getFirstNameFromParams,
  getPaymentTypeFromParams,
  parseQueryParams,
} from '@/utils/paymentUtils'
import { useRouter, useSearchParams } from 'next/navigation'
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export default function Page() {
  return (
    <Suspense fallback={<Spinner message='Caricamento...' />}>
      <ConfirmContent />
    </Suspense>
  )
}

function ConfirmContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const hasProcessedRef = useRef(false)
  const [status, setStatus] = useState('processing')
  const [error, setError] = useState(null)
  const [isPopup, setIsPopup] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsPopup(window.opener && !window.opener.closed)
    }
  }, [])

  const queryParams = useMemo(
    () => parseQueryParams(searchParams),
    [searchParams]
  )
  const isSinglePayment = useMemo(
    () => getPaymentTypeFromParams(queryParams) === 'single',
    [queryParams]
  )

  const notifyParent = useCallback(
    (success, data = {}, errorInfo = null) => {
      if (typeof window === 'undefined') return false

      const message = {
        type: 'PAYMENT_CONFIRM_RESULT',
        success,
        data,
        error: errorInfo,
        paymentType: isSinglePayment ? 'single' : 'recurrent',
        firstName: getFirstNameFromParams(queryParams),
      }

      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage(message, window.location.origin)
          setTimeout(() => window.close(), POPUPS_CLOSE_DELAY_MS)
          return true
        } catch (err) {
          debugLogger.error('ConfirmPage', 'Error notifying parent', err)
          return false
        }
      }

      return false
    },
    [isSinglePayment]
  )

  useEffect(() => {
    if (hasProcessedRef.current) return
    hasProcessedRef.current = true

    const confirmPayment = async () => {
      debugLogger.log('ConfirmPage', 'Starting confirmation', {
        isSinglePayment,
        queryParams,
      })

      try {
        let confirmData = {}

        if (isSinglePayment) {
          confirmData = {
            ...queryParams,
            success: '1',
          }
        } else {
          confirmData = {
            ...queryParams,
            companyname: queryParams.companyname || '',
            paymentmethod: 'iframe',
            from: 'iframe',
            success: '1',
          }
        }

        debugLogger.log('ConfirmPage', 'Calling confirm API', confirmData)
        const result = await PaymentAPIService.confirmPayment(confirmData)
        debugLogger.log('ConfirmPage', 'Confirm result', result)

        if (result.success) {
          setStatus('success')
          const notified = notifyParent(true, result)
          if (!notified) {
            setTimeout(() => {
              const firstName = getFirstNameFromParams(queryParams)
              router.push(
                `/lnd/successo${firstName ? `?firstName=${encodeURIComponent(firstName)}` : ''}`
              )
            }, REDIRECT_DELAY_MS)
          }
        } else {
          throw new Error(
            result.error?.message || result.message || 'Confirmation failed'
          )
        }
      } catch (err) {
        debugLogger.error('ConfirmPage', 'Confirmation failed', err)
        setStatus('error')
        setError(err.message || 'Errore durante la conferma')

        const notified = notifyParent(
          false,
          {},
          { message: err.message, code: err.code }
        )
        if (!notified) {
          setTimeout(() => {
            const firstName = getFirstNameFromParams(queryParams)
            router.push(
              `/lnd/errore${firstName ? `?firstName=${encodeURIComponent(firstName)}` : ''}`
            )
          }, ERROR_REDIRECT_DELAY_MS)
        }
      }
    }

    confirmPayment()
  }, [isSinglePayment, queryParams, notifyParent, router])

  return (
    <div
      className='confirm-page'
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        background: '#f5f5f5',
      }}
    >
      {status === 'processing' && (
        <>
          <Spinner />
          <p style={{ marginTop: '1rem', color: '#666' }}>
            Conferma pagamento in corso...
          </p>
          {isPopup && (
            <p
              style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#999' }}
            >
              (Popup - notificherà la finestra principale)
            </p>
          )}
        </>
      )}

      {status === 'success' && (
        <>
          <p style={{ color: 'green', fontSize: '1.5rem' }}>✓</p>
          <p style={{ color: 'green', fontSize: '1.2rem' }}>
            Pagamento confermato!
          </p>
          <p style={{ marginTop: '0.5rem', color: '#666' }}>
            {isPopup ? 'Chiusura in corso...' : 'Reindirizzamento...'}
          </p>
        </>
      )}

      {status === 'error' && (
        <>
          <p style={{ color: 'red', fontSize: '1.5rem' }}>✗</p>
          <p style={{ color: 'red', fontSize: '1.2rem' }}>
            Errore nella conferma
          </p>
          <p style={{ marginTop: '0.5rem', color: '#666' }}>{error}</p>
          <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#999' }}>
            {isPopup
              ? 'La finestra si chiuderà automaticamente...'
              : 'Reindirizzamento in corso...'}
          </p>
        </>
      )}
    </div>
  )
}
